"use client";

import { useCallback, useEffect, useRef } from "react";
import { PropertyTypeV2 } from "commonModule/services/common";
import NewLocationDot from "components/icons-svg/NewLocationDot";
export interface PropertyMapTypes {
  property: PropertyTypeV2;
  isDetailPage?: boolean;
}
const maxAllowedZoom = 18;
const PropertyMap = ({
  property,
  isDetailPage = true
}: PropertyMapTypes) => {
  const initMap = useCallback(() => {
    // Check if Google Maps is available
    if (typeof window === "undefined" || typeof google === "undefined" || typeof google.maps === "undefined") {
      console.warn("Google Maps API is not available");
      return;
    }

    // Check if map element exists
    const mapElement = document?.getElementById("mapCircle");
    if (!mapElement) {
      console.warn("Map element not found");
      return;
    }
    const center = {
      lat: Number(property?.latitude),
      lng: Number(property?.longitude)
    };
    try {
      const map = new google.maps.Map(mapElement, {
        zoom: 15,
        minZoom: 3,
        maxZoom: maxAllowedZoom,
        center,
        // Keep the buttons:
        disableDefaultUI: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER
        },
        //  Block mouse wheel/trackpad scroll zoom:
        scrollwheel: false,
        // Require Ctrl/⌘ for scroll-to-zoom and avoid accidental pinch-zoom;
        // still allows panning:
        gestureHandling: "cooperative",
        // Block double-click zoom:
        disableDoubleClickZoom: true,
        // (Optional) block +/- keyboard zoom:
        // keyboardShortcuts: false,

        mapTypeControl: false,
        rotateControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [{
          featureType: "road",
          elementType: "labels",
          stylers: [{
            visibility: "on"
          }]
        }, {
          featureType: "road",
          elementType: "geometry",
          stylers: [{
            visibility: "on"
          }]
        }, {
          featureType: "water",
          elementType: "geometry.fill",
          stylers: [{
            color: "#cae8f2"
          }]
        }]
      });
      // Prevent zooming in past allowed level
      map.addListener("zoom_changed", () => {
        const currentZoom = map.getZoom();
        if (typeof currentZoom === "number" && currentZoom > maxAllowedZoom) {
          map.setZoom(maxAllowedZoom);
        }
      });

      // Disable full-screen control for the Street View panorama
      const streetView = map?.getStreetView();
      streetView?.setOptions({
        fullscreenControl: false
      });
      new google.maps.Marker({
        position: center,
        map
      });
    } catch (error) {
      console.error("Error initializing Google Map:", error);
    }
  }, [property?.latitude, property?.longitude]);
  const retryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const maxRetries = 20; // Maximum 10 seconds (20 * 500ms)
  const retryCountRef = useRef(0);
  useEffect(() => {
    // Reset retry count for new effect run
    retryCountRef.current = 0;

    // Check if window is available and Google Maps is already loaded
    if (typeof window !== "undefined" && window?.googleMapsLoaded === true) {
      initMap();
      return;
    }

    // Set up retry logic with polling
    retryIntervalRef.current = setInterval(() => {
      retryCountRef.current += 1;
      if (typeof window !== "undefined" && window?.googleMapsLoaded === true) {
        initMap();
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
      } else if (retryCountRef.current >= maxRetries) {
        // Stop retrying after max attempts
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
      }
    }, 500); // Check every 500ms

    // Cleanup interval on unmount or dependency change
    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      retryCountRef.current = 0;
    };
  }, [initMap]);
  return <div className='mb-2.5' data-sentry-component="PropertyMap" data-sentry-source-file="PropertyMap.tsx">
      {isDetailPage && <div className='mb-4 flex items-center gap-2 md:mb-6'>
          <NewLocationDot className='h-4 w-3 fill-[#707281] md:h-5 md:w-4' />
          <h3 className='text-base font-semibold leading-[19.2px] tracking-[-0.24px] text-secondary500 md:text-xl md:leading-6 md:tracking-[-0.3px]'>
            Location highlights
          </h3>
        </div>}
      <div id='mapCircle' className={`${isDetailPage ? "h-[216px] sm:h-[250px] sxm:h-[320px] md:h-[400px] xl2:h-[450px]" : "h-[246px]"} w-full rounded-lg`} />
    </div>;
};
export default PropertyMap;